import { useState, useRef, useEffect } from 'react';
import { X, FileText, Upload, Trash2, File } from 'lucide-react';
import { App } from 'antd';

interface ResumeUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFileSelect?: (file: File) => void;
}

// 支持的文本文件类型（不再支持.doc）
const ALLOWED_FILE_TYPES = [
  'text/plain',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
];

const ALLOWED_EXTENSIONS = ['.txt', '.md', '.docx', '.pdf'];

// 最大文件大小 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default function ResumeUploadModal({
  isOpen,
  onClose,
  onFileSelect,
}: ResumeUploadModalProps) {
  const { message } = App.useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // 阻止背景页面滚动
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // 重置状态
  const resetState = () => {
    setSelectedFile(null);
    setDragActive(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    resetState();
    onClose();
  };

  // 验证文件
  const validateFile = (file: File): string | null => {
    // 检查文件类型
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      // 通过后缀名再次检查
      const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
        file.name.toLowerCase().endsWith(ext),
      );
      if (!hasValidExtension) {
        return '不支持的文件格式，请上传 TXT、MD、DOCX 或 PDF 文件';
      }
    }

    // 检查文件大小
    if (file.size > MAX_FILE_SIZE) {
      return '文件大小不能超过 10MB';
    }

    return null;
  };

  // 处理文件选择
  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      message.error(error);
      return;
    }

    setSelectedFile(file);
  };

  // 处理文件输入变化
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // 处理拖拽事件
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // 确认选择文件
  const handleConfirm = () => {
    if (!selectedFile) {
      message.error('请先选择文件');
      return;
    }

    onFileSelect?.(selectedFile);
    handleClose();
  };

  // 移除已选文件
  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" />

      {/* 弹窗 */}
      <div className="relative w-full max-w-md bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden animate-scale-in">
        {/* 顶部装饰 */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500" />

        {/* 关闭按钮 */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg p-1 transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {/* 标题 */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white">选择简历</h2>
            <p className="text-gray-400 text-sm mt-1">
              支持 TXT、MD、DOCX、PDF 格式
            </p>
          </div>

          {/* 文件上传区域 */}
          {!selectedFile ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                dragActive
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-gray-600 hover:border-gray-500 hover:bg-gray-800/50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_EXTENSIONS.join(',')}
                onChange={handleFileChange}
                className="hidden"
              />
              <Upload className="w-8 h-8 mx-auto mb-3 text-gray-500" />
              <p className="text-white font-medium mb-1">点击或拖拽上传文件</p>
              <p className="text-gray-500 text-xs">最大支持 10MB</p>
            </div>
          ) : (
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <File className="w-5 h-5 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleClose}
              className="flex-1 py-2.5 text-gray-300 hover:text-white bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg transition-all cursor-pointer"
            >
              取消
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedFile}
              className="flex-1 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
            >
              确认选择
            </button>
          </div>
        </div>
      </div>

      {/* 动画样式 */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
